<script lang="ts" setup>
import { computed, getCurrentInstance, ref } from "vue";

const props = defineProps({
  keyboardAccessible: {
    type: Boolean,
    default: false,
  },
});

const tooltip = ref<HTMLElement>();
const container = ref<HTMLElement>();
const isDismissed = ref(false);
const viewportPadding = 8;
const tooltipGap = 4;
const tooltipId = `tooltip-${getCurrentInstance()?.uid ?? "unknown"}`;

type BoundaryRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

const isPlacedAtBottom = computed(() =>
  container.value ? container.value.classList.contains("bottom") : false,
);

const getBoundaryRect = (element: HTMLElement): BoundaryRect => {
  let current = element.parentElement;

  while (current && current !== document.body) {
    const styles = window.getComputedStyle(current);
    const clipsX = !["visible", "clip"].includes(styles.overflowX);
    const clipsY = !["visible", "clip"].includes(styles.overflowY);

    if (clipsX || clipsY) {
      const rect = current.getBoundingClientRect();
      return {
        top: rect.top + viewportPadding,
        right: rect.right - viewportPadding,
        bottom: rect.bottom - viewportPadding,
        left: rect.left + viewportPadding,
      };
    }

    current = current.parentElement;
  }

  return {
    top: viewportPadding,
    right: window.innerWidth - viewportPadding,
    bottom: window.innerHeight - viewportPadding,
    left: viewportPadding,
  };
};

const setPosition = () => {
  if (!tooltip.value || !container.value) {
    return;
  }

  requestAnimationFrame(() => {
    if (!tooltip.value || !container.value) {
      return;
    }

    if (isPlacedAtBottom.value) {
      tooltip.value.style.bottom = `-${tooltip.value.offsetHeight}px`;
      tooltip.value.style.top = "auto";
    } else {
      tooltip.value.style.top = `-${tooltip.value.offsetHeight}px`;
      tooltip.value.style.bottom = "auto";
    }

    const containerRect = container.value.getBoundingClientRect();
    const boundaryRect = getBoundaryRect(container.value);
    const tooltipHeight = tooltip.value.offsetHeight;
    const tooltipWidth = tooltip.value.offsetWidth;
    const availableAbove = containerRect.top - boundaryRect.top;
    const availableBelow = boundaryRect.bottom - containerRect.bottom;
    const placeAtBottom =
      isPlacedAtBottom.value ||
      (availableAbove < tooltipHeight + tooltipGap &&
        availableBelow > availableAbove);

    if (placeAtBottom) {
      tooltip.value.style.top = `${containerRect.height + tooltipGap}px`;
      tooltip.value.style.bottom = "auto";
    } else {
      tooltip.value.style.top = `${-(tooltipHeight + tooltipGap)}px`;
      tooltip.value.style.bottom = "auto";
    }

    let leftOffset = (containerRect.width - tooltipWidth) / 2;

    if (container.value.classList.contains("left")) {
      leftOffset = 0;
    } else if (container.value.classList.contains("right")) {
      leftOffset = containerRect.width - tooltipWidth;
    }

    const minLeftOffset = boundaryRect.left - containerRect.left;
    const maxLeftOffset =
      boundaryRect.right - containerRect.left - tooltipWidth;

    if (maxLeftOffset < minLeftOffset) {
      leftOffset = minLeftOffset;
    } else {
      leftOffset = Math.min(Math.max(leftOffset, minLeftOffset), maxLeftOffset);
    }

    tooltip.value.style.left = `${leftOffset}px`;
    tooltip.value.style.right = "auto";
    tooltip.value.style.transform = "none";
  });
};

const handleFocus = () => {
  isDismissed.value = false;
  setPosition();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.keyboardAccessible || event.key !== "Escape") {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDismissed.value = true;
};
</script>

<template>
  <div
    class="tooltip_container"
    :class="{
      keyboard_accessible: props.keyboardAccessible,
      tooltip_dismissed: isDismissed,
    }"
    ref="container"
    :tabindex="props.keyboardAccessible ? 0 : undefined"
    :aria-describedby="props.keyboardAccessible ? tooltipId : undefined"
    @focus="handleFocus"
    @blur="isDismissed = false"
    @keydown="handleKeydown"
  >
    <div @mouseover="setPosition">
      <slot class="text"></slot>
    </div>
    <div
      class="tooltip"
      ref="tooltip"
      :id="props.keyboardAccessible ? tooltipId : undefined"
      :role="props.keyboardAccessible ? 'tooltip' : undefined"
    >
      <slot name="tooltip"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tooltip_container {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  top: 3px;
  font-size: 0.8rem;
  line-height: 1.8;
  background: var(--classic-tooltip-surface, #404040);
  color: #fff;
  border-radius: 0.3rem;
  box-sizing: border-box;
  padding: 0 0.4rem;
  top: -1.7rem;
  left: 50%;
  transform: translate(-50%, 0);
  display: none;
  white-space: nowrap;
  z-index: 99;
}

.tooltip_container.multiline .tooltip {
  width: 400px;
  white-space: pre-line;
}

.tooltip_container.bottom .tooltip {
  bottom: -1.5em;
  top: auto;
}

.tooltip_container.left .tooltip {
  transform: none;
  left: 0;
}

.tooltip_container.right .tooltip {
  left: auto;
  right: 0;
  transform: none;
}

.tooltip_container:hover .tooltip {
  display: block;
}

.tooltip_container.keyboard_accessible:focus .tooltip {
  display: block;
}

.tooltip_container.keyboard_accessible.tooltip_dismissed .tooltip {
  display: none;
}

.tooltip_container.keyboard_accessible:focus-visible {
  border-radius: 2px;
  outline: 2px solid var(--classic-focus, #3273dc);
  outline-offset: 2px;
}

.tooltip_container :deep(span) {
  vertical-align: middle;
}

.delayed .tooltip {
  opacity: 0;

  animation: fadeIn 0s;
  animation-delay: 0.7s;
  animation-fill-mode: forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
